import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import config from '../../config/config';
import { useBranding } from '../../context/BrandingContext';
import { useTariff } from '../../context/TariffContext';
import ToastMessage from '../../shared-components/toast-message/ToastMessage';
import getFirstSubdomain from '../../shared-functions/DomiansplitFunction';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../shared-functions/LocalStorage';
import charger, { AdhocSessionPayload } from '../../utils/api/charger';
import hyperPay from '../../utils/api/hyperPay';
import { normalizeAdhocParameters } from '../../utils/brandingUtils';

// Component imports
import ChargingStage from './components/ChargingStage';
import ChargingStatus from './components/ChargingStatus';
import GenerateHyperPay from './components/hyperpay';
import Invoice from './components/Invoice';
import PaymentMethod from './components/PaymentMethod';

interface ConnectorInterface {
  _id: string;
  connector: string;
  connectorId: string | number;
  isReservationEnabled: boolean;
  status: string;
}

interface PricingDetailsProperties {
  currentPrice: string;
  vat?: number | string;
  dynamicPricing: {
    price: string;
    priceType: string;
    timings: {
      startDateUtc: string | null;
      endDateUtc: string | null;
    }[];
  }[];
}

interface DurationAmount {
  from: number;
  to: number;
  mode: 'grace' | 'flatRate' | 'perMinute';
  amount: number;
}

interface IdleFeePrices {
  durationAmountList: DurationAmount[];
  ceilingValue: number;
}

type StageStatus = {
  inProgress: boolean;
  done: boolean;
};

type FormStageState = {
  paymentDetails: StageStatus;
  charging: StageStatus;
  invoice: StageStatus;
};

type PaymentDetails = {
  paymentMethodId?: string;
  last4?: string;
  expiryMonth?: number;
  expiryYear?: number;
  funding?: string;
  brand?: string;
  cardHolderName: string;
};

/**
 * Cleans up local session storage when exiting the invoice
 */
const cleanStorageAndRedirect = () => {
  removeLocalStorageItem('invoiceRemove');
  removeLocalStorageItem('token');
  removeLocalStorageItem('adhocToken');
  removeLocalStorageItem('adhocUid');
  removeLocalStorageItem('sessionId');
  removeLocalStorageItem('paymentMethod');
  removeLocalStorageItem('customer.browser.deviceId');
  removeLocalStorageItem('chargingStatus');
  removeLocalStorageItem('guestToken');
  removeLocalStorageItem('selectedConnector');
  removeLocalStorageItem('paidAmount');
  removeLocalStorageItem('totalKwhConsumer');
  removeLocalStorageItem('liveCost');
  removeLocalStorageItem('liveHours');
  removeLocalStorageItem('liveMinutes');
  removeLocalStorageItem('liveSeconds');
  removeLocalStorageItem('currency');
  removeLocalStorageItem('idleState');
  removeLocalStorageItem('connectionStartTime');
};

/**
 * Generates and stores a new adhoc session token in localStorage
 * @param payload Optional session payload (locationId, evseUid, connectorId)
 */
const storeNewAdhocToken = async (payload?: AdhocSessionPayload) => {
  try {
    const response = await charger.createAdhocSession(payload);
    const data = response?.data?.data || response?.data;
    if (data?.token) {
      setLocalStorageItem('token', data.token);
      setLocalStorageItem('adhocToken', data.token);
      if (data?.adhocUid) {
        setLocalStorageItem('adhocUid', data.adhocUid);
      }
      return data.token;
    }
  } catch (error) {
    console.error('Error generating and storing new adhoc session token:', error);
  }
  return null;
};

/**
 * Charger component
 * @returns React Component
 */
function Charger() {
  const domain = getFirstSubdomain();
  const { brandData } = useBranding();
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails>();
  const [chargingStatus, setChargingStatus] = useState<boolean>(false);
  const appVersion = getLocalStorageItem('appVersion') || '';

  const { deviceId: routeDeviceId } = useParams<{ deviceId: string }>();

  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);

  const {
    deviceId,
    domain: domainParameter,
    connectorId: connectorIdFromDevice,
  } = normalizeAdhocParameters({
    deviceId: routeDeviceId || queryParameters.get('deviceId'),
    domain: queryParameters.get('domain'),
    connectorId: queryParameters.get('connectorId'),
  });

  const hyperPayId = queryParameters.get('id');

  const sessionPayload: AdhocSessionPayload = {
    ...(deviceId && { evseUid: deviceId }),
    ...(connectorIdFromDevice && { connectorId: connectorIdFromDevice }),
  };

  useEffect(() => {
    if (connectorIdFromDevice) {
      setLocalStorageItem('selectedConnector', connectorIdFromDevice);
    }
  }, [connectorIdFromDevice]);

  const {
    fees: tariffFees,
    energyPrice: tariffEnergyPrice,
    vat: tariffVat,
    isFreeCharging: tariffIsFreeCharging,
    isLoaded: isTariffLoaded,
  } = useTariff();

  const locationFees = tariffFees;

  const [connectors, setConnectors] = useState<ConnectorInterface[]>([]);
  const [pricingDetails, setPricingDetails] = useState<PricingDetailsProperties>({
    currentPrice: '',
    dynamicPricing: [],
  });
  const [idleFeePrices, setIdleFeePrices] = useState<IdleFeePrices | null>(null);
  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });

  const [formStage, setFormStage] = useState<FormStageState>(() => {
    if (hyperPayId) {
      return {
        paymentDetails: {
          inProgress: false,
          done: true,
        },
        charging: {
          inProgress: true,
          done: false,
        },
        invoice: {
          inProgress: false,
          done: false,
        },
      };
    }
    return {
      paymentDetails: {
        inProgress: true,
        done: false,
      },
      charging: {
        inProgress: false,
        done: false,
      },
      invoice: {
        inProgress: false,
        done: false,
      },
    };
  });
  const [isSessionChecked, setIsSessionChecked] = useState(false);
  const [isSessionInitialized, setIsSessionInitialized] = useState<boolean>(false);
  const [paymentRequiredFallback, setPaymentRequiredFallback] = useState<boolean>(false);

  useEffect(() => {
    if (tariffEnergyPrice || tariffVat !== undefined) {
      setPricingDetails((previous) => ({
        ...previous,
        currentPrice: tariffEnergyPrice || previous.currentPrice,
        vat: tariffVat !== undefined ? tariffVat : previous.vat,
      }));
    }
  }, [tariffEnergyPrice, tariffVat]);

  useEffect(() => {
    if (getLocalStorageItem('invoiceRemove')) {
      cleanStorageAndRedirect();
    }
  }, []);
  const isFreeCharging = useMemo(() => {
    if (!isTariffLoaded || !locationFees || paymentRequiredFallback) return false;
    return tariffIsFreeCharging;
  }, [isTariffLoaded, locationFees, tariffIsFreeCharging, paymentRequiredFallback]);

  /**
   * Fetch the list of connectors for the device
   */
  const getConnectors = async () => {
    try {
      const existingToken = getLocalStorageItem('token') || getLocalStorageItem('adhocToken');
      if (!existingToken && !hyperPayId) {
        await storeNewAdhocToken(sessionPayload);
      }

      const response = await charger.fetchLocationByEvse(deviceId ?? '');
      const apiResponse = response.data;
      const locationData = apiResponse?.location || apiResponse?.data?.location || apiResponse;

      // Match current EVSE
      const evsesList = locationData?.evses || [];
      const currentEvse =
        evsesList.find(
          (evseItem: any) => evseItem.evse_id === deviceId || evseItem.uid === apiResponse?.evseUid,
        ) || evsesList[0];

      const evseStatus = currentEvse?.status || apiResponse?.status || 'AVAILABLE';

      let mappedConnectors: any[] = [];
      if (currentEvse?.connectors?.length > 0) {
        mappedConnectors = currentEvse.connectors.map((conn: any, index: number) => {
          const isDc =
            conn?.power_type?.toUpperCase() === 'DC' ||
            conn?.standard?.toLowerCase().includes('dc');

          const maxKw = conn?.max_electric_power
            ? `${(conn.max_electric_power / 1000).toFixed(0)} kW`
            : '';

          const standardName = conn?.standard || '';

          return {
            _id: conn?.id || String(index + 1),
            connectorId: conn?.id || String(index + 1),
            connector: standardName
              ? `${standardName} (${isDc ? 'DC' : 'AC'})`
              : `Connector ${conn?.id || index + 1}`,
            status: evseStatus.toUpperCase(),
            isReservationEnabled: evseStatus.toUpperCase() === 'AVAILABLE',
            capacity: maxKw,
            currentType: isDc ? 'DC' : 'AC',
            currentForm: conn?.format || '',
            locationId: locationData?.id || '',
            evseUid: currentEvse?.uid || apiResponse?.evseUid || '',
            tariffIds: conn?.tariff_ids || [],
          };
        });
      } else if (apiResponse?.connectors) {
        mappedConnectors = apiResponse.connectors.map((connector: ConnectorInterface) => ({
          ...connector,
          locationId:
            (connector as any)?.locationId || apiResponse?.locationId || locationData?.id || '',
          evseUid:
            (connector as any)?.evseUid ||
            (connector as any)?.evseId ||
            apiResponse?.evseUid ||
            currentEvse?.uid ||
            '',
          connector: `Connector ${connector?.connectorId}`,
          isReservationEnabled: connector?.status === 'Available',
        }));
      }

      setConnectors(mappedConnectors);
      setPricingDetails((previous) => ({
        currentPrice: tariffEnergyPrice || apiResponse?.currentPrice || previous.currentPrice || '',
        vat: tariffVat !== undefined ? tariffVat : (apiResponse?.vat ?? previous.vat ?? 0),
        dynamicPricing: apiResponse?.dynamicPricing || [],
      }));
      setIdleFeePrices(apiResponse?.penaltyDetails || null);
    } catch (error: any) {
      const isTokenError =
        error?.response?.status === 401 ||
        error?.response?.data?.message?.includes('PENDING_REGISTRATION') ||
        error?.response?.data?.message?.includes('Adhoc token');

      if (isTokenError) {
        await storeNewAdhocToken(sessionPayload);
      }
      setToastStatus({
        openToast: true,
        showToastMessage: error?.response?.data?.message,
      });
    }
  };

  /**
   * Function to set the formStatus as charging session
   */
  const navigateToChargingSession = () => {
    setFormStage({
      paymentDetails: {
        inProgress: false,
        done: true,
      },
      charging: {
        inProgress: true,
        done: false,
      },
      invoice: {
        inProgress: false,
        done: false,
      },
    });
  };

  /**
   * Function to set the formStatus as charging session
   */
  const navigateToInvoice = () => {
    setFormStage({
      paymentDetails: {
        inProgress: false,
        done: true,
      },
      charging: {
        inProgress: false,
        done: true,
      },
      invoice: {
        inProgress: true,
        done: false,
      },
    });
  };

  /**
   * Resets session to start screen (Payment Details for paid charging, Pre-Charge for free charging)
   * and preserves the adhoc session token in localStorage.
   * @param removeQueryParameters Whether to remove query parameters from the URL
   */
  const navigateToPaymentMethod = async (removeQueryParameters = false) => {
    if (removeQueryParameters) {
      window.history.replaceState(
        {},
        '',
        domainParameter
          ? `${window.location.pathname}?domain=${domainParameter}`
          : window.location.pathname,
      );
    }

    const currentConnector = getLocalStorageItem('selectedConnector') || connectorIdFromDevice;

    // Clean active session items
    removeLocalStorageItem('chargingStatus');
    removeLocalStorageItem('sessionId');
    removeLocalStorageItem('adhocUid');
    removeLocalStorageItem('selectedConnector');
    removeLocalStorageItem('connectionStartTime');
    removeLocalStorageItem('commandId');
    removeLocalStorageItem('paidAmount');
    removeLocalStorageItem('totalKwhConsumer');
    removeLocalStorageItem('liveCost');
    removeLocalStorageItem('liveHours');
    removeLocalStorageItem('liveMinutes');
    removeLocalStorageItem('liveSeconds');
    removeLocalStorageItem('idleState');

    // Reset payment details so a fresh payment method can be selected
    removeLocalStorageItem('paymentCardDetails');
    removeLocalStorageItem('paymentMethod');
    setPaymentDetails(undefined);

    // Immediately switch formStage to eliminate UI freeze
    if (isFreeCharging && !paymentRequiredFallback) {
      // Free charging start screen is PreChargeConfig within ChargingStatus
      setFormStage({
        paymentDetails: {
          inProgress: false,
          done: true,
        },
        charging: {
          inProgress: true,
          done: false,
        },
        invoice: {
          inProgress: false,
          done: false,
        },
      });
    } else {
      // Paid charging start screen is PaymentMethod / GenerateHyperPay
      setFormStage({
        paymentDetails: {
          inProgress: true,
          done: false,
        },
        charging: {
          inProgress: false,
          done: false,
        },
        invoice: {
          inProgress: false,
          done: false,
        },
      });
    }

    // Refresh token in background only if completely missing
    const existingToken = getLocalStorageItem('token') || getLocalStorageItem('adhocToken');
    if (!existingToken) {
      const refreshPayload: AdhocSessionPayload = {
        ...(deviceId && { evseUid: deviceId }),
        ...(currentConnector && { connectorId: currentConnector }),
      };
      await storeNewAdhocToken(refreshPayload);
      await getConnectors();
    }
  };

  /**
   * Handle payment required fallback
   */
  const handlePaymentRequiredFallback = () => {
    setPaymentRequiredFallback(true);
    navigateToPaymentMethod(false);
  };

  useEffect(() => {
    if (domainParameter) {
      setLocalStorageItem('domain', domainParameter);
    }
  }, [domainParameter]);

  /**
   * Function to check the status of the charger
   */
  const checkStatusCharger = async () => {
    if (hyperPayId) {
      setIsSessionChecked(true);
      return;
    }

    try {
      const token = getLocalStorageItem('token') || getLocalStorageItem('adhocToken');
      if (!token) {
        await storeNewAdhocToken(sessionPayload);
        setIsSessionChecked(true);
        return;
      }
      const response = await charger.getTransaction({});
      setIsSessionChecked(true);
      const rawData = response?.data?.data || response?.data;
      const activeSession = rawData?.active || rawData;
      const sessionStatus = activeSession?.status;

      if (!sessionStatus && !activeSession?.sessionId && !rawData?.active) {
        const connectionStartTime = getLocalStorageItem('connectionStartTime');
        if (connectionStartTime) {
          const elapsed = (Date.now() - Number(connectionStartTime)) / 1000;
          if (elapsed < 90) {
            setChargingStatus(false);
            navigateToChargingSession();
            return;
          }
        }
        return;
      }

      const energyKwh =
        activeSession?.energyKwh ??
        activeSession?.powerConsumedInKwh ??
        activeSession?.powerConsumed ??
        0;
      const paidAmount =
        activeSession?.paidAmount ?? activeSession?.totalCost ?? activeSession?.liveCost ?? 0;

      if (activeSession?.sessionId) {
        setLocalStorageItem('sessionId', activeSession.sessionId);
      }

      switch (sessionStatus) {
        case 'onHold':
          setLocalStorageItem('paidAmount', paidAmount);
          setLocalStorageItem('totalKwhConsumer', energyKwh);
          setChargingStatus(false);
          navigateToChargingSession();
          break;

        case 'inProgress':
          setLocalStorageItem('paidAmount', paidAmount);
          setLocalStorageItem('totalKwhConsumer', energyKwh);
          setChargingStatus(true);
          navigateToChargingSession();
          break;

        case 'finished':
        case 'Completed':
        case 'Finished':
          setLocalStorageItem('paidAmount', paidAmount);
          setLocalStorageItem('totalKwhConsumer', energyKwh);
          navigateToInvoice();
          break;

        default: {
          if (
            activeSession &&
            (activeSession?.energyKwh !== undefined ||
              activeSession?.totalCost !== undefined ||
              activeSession?.sessionId)
          ) {
            setLocalStorageItem('paidAmount', paidAmount);
            setLocalStorageItem('totalKwhConsumer', energyKwh);
            setChargingStatus(true);
            navigateToChargingSession();
            break;
          }
          const connectionStartTime = getLocalStorageItem('connectionStartTime');
          if (connectionStartTime) {
            const elapsed = (Date.now() - Number(connectionStartTime)) / 1000;
            if (elapsed < 90) {
              setChargingStatus(false);
              navigateToChargingSession();
              break;
            }
          }
        }
      }
    } catch (error: any) {
      const isTokenError =
        error?.response?.status === 401 ||
        error?.response?.data?.message?.includes('PENDING_REGISTRATION') ||
        error?.response?.data?.message?.includes('Adhoc token');

      if (isTokenError) {
        await storeNewAdhocToken(sessionPayload);
      }
      setIsSessionChecked(true);
      console.error('Error fetching live transaction:', error);
    }
  };

  /**
   * Initializes adhoc session token if not already present
   */
  const initAdhocSession = async () => {
    const existingToken = getLocalStorageItem('token') || getLocalStorageItem('adhocToken');
    if (!existingToken && !hyperPayId) {
      await storeNewAdhocToken(sessionPayload);
    }
    setIsSessionInitialized(true);
  };

  useEffect(() => {
    initAdhocSession();
  }, []);

  useEffect(() => {
    if (!isSessionInitialized || !deviceId) {
      return undefined;
    }

    checkStatusCharger();
    getConnectors();

    const pollInterval = setInterval(() => {
      checkStatusCharger();
    }, 10000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [isSessionInitialized, deviceId]);

  useEffect(() => {
    if (hyperPayId) {
      hyperPay
        .confirmRegistration({
          checkoutId: hyperPayId,
        })
        .then((response: any) => {
          const data = response?.data?.data || response?.data;
          const cardInfo = data?.card || data?.paymentDetails || data;
          const last4 =
            cardInfo?.last4Digits || cardInfo?.last4 || data?.last4Digits || data?.last4 || '';
          const brand =
            cardInfo?.brand ||
            cardInfo?.paymentBrand ||
            data?.brand ||
            data?.paymentBrand ||
            cardInfo?.cardType ||
            '';
          const cardHolder = cardInfo?.holder || cardInfo?.cardHolderName || data?.holder || '';

          if (last4 || brand) {
            const details = {
              last4,
              brand,
              cardHolderName: cardHolder,
            };
            setPaymentDetails(details);
            setLocalStorageItem('paymentCardDetails', details);
          }
          navigateToChargingSession();
        })
        .catch(async (error: any) => {
          console.error('Error confirming registration:', error);
          setToastStatus({
            openToast: true,
            showToastMessage: error?.response?.data?.message || 'Payment registration failed',
          });
          await navigateToPaymentMethod(true);
        });
    }
  }, [hyperPayId]);

  useEffect(() => {
    if (
      isSessionChecked &&
      isFreeCharging &&
      !formStage.charging.inProgress &&
      !formStage.invoice.inProgress
    ) {
      navigateToChargingSession();
    }
  }, [
    isSessionChecked,
    isFreeCharging,
    formStage.charging.inProgress,
    formStage.invoice.inProgress,
  ]);

  return (
    <div className="charger-wrap">
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() =>
          setToastStatus({
            openToast: false,
            showToastMessage: '',
          })
        }
        type="error"
        message={toastStatus.showToastMessage || 'Something went wrong'}
        onCloseTimer={6000}
      />
      {!formStage.invoice.inProgress && <ChargingStage formStage={formStage} />}
      {formStage.paymentDetails.inProgress &&
        locationFees !== null &&
        !isFreeCharging &&
        (domain && brandData?.guestPaymentMethod
          ? brandData?.guestPaymentMethod
          : config.guestPaymentMode) === 'stripe' && (
          <PaymentMethod
            navigateToChargingSession={navigateToChargingSession}
            setPaymentDetails={setPaymentDetails}
            navigateToInvoice={navigateToInvoice}
            pricingDetails={pricingDetails}
            idleFeePrices={idleFeePrices}
            appVersion={appVersion}
            fees={locationFees}
            isFreeCharging={isFreeCharging}
          />
        )}
      {formStage.paymentDetails.inProgress &&
        locationFees !== null &&
        !isFreeCharging &&
        domain &&
        (domain && brandData?.guestPaymentMethod
          ? brandData?.guestPaymentMethod
          : config.guestPaymentMode) !== 'stripe' && (
          <GenerateHyperPay
            pricingDetails={pricingDetails}
            fees={locationFees}
            deviceId={deviceId}
            connectors={connectors}
          />
        )}
      {formStage.charging.inProgress && (
        <ChargingStatus
          paymentDetails={paymentDetails}
          navigateToPaymentMethod={navigateToPaymentMethod}
          navigateToInvoice={navigateToInvoice}
          chargingStatus={chargingStatus}
          connectors={connectors}
          isFreeCharging={isFreeCharging}
          currentPrice={tariffEnergyPrice || pricingDetails.currentPrice}
          vat={tariffVat ?? pricingDetails.vat ?? locationFees?.vat}
          appVersion={appVersion}
          fees={locationFees}
          onPaymentRequired={handlePaymentRequiredFallback}
        />
      )}
      {formStage.invoice.inProgress && <Invoice />}
    </div>
  );
}

export default Charger;
